// StepsBottomBar.jsx
import React, { useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserProfileContext } from '../../contexts/UserProfileContext';
import AssetBottomNav from './AssetBottomNav';

/**
 * steps: an array of objects:
 *   { stepNumber: 1, label: "Preparation", status: "done|ongoing|upcoming", link: "/flow/..." }
 * 
 * We show a left button (50x50) with a dropup menu "Home" / "Profile". 
 * The rest is horizontally scrollable steps, each with a colored stepNumber 
 * and a label, separated by partial-height vertical dividers. 
 * 
 * We'll auto-center the "ongoing" step if we find one, or fallback to the last "done" step if none is ongoing.
 */
export default function StepsBottomBar({ steps = [] }) {

  // references for the scroll container and the step items
  const scrollContainerRef = useRef(null);
  const stepItemRefs = useRef([]); // array of refs

  const { userProfile, setShowProfileModal, clearUserProfile } = useContext(UserProfileContext);
  const displayName = userProfile.given_name && userProfile.family_name 
    ? `${userProfile.given_name} ${userProfile.family_name}` 
    : userProfile.given_name || 'Profile';

  function handleProfileClick() {
    setShowProfileModal(true);
  }

  function handleLogout() {
    clearUserProfile();
  }

  // We define the logic to find "ongoing" or the last "done" step
  useEffect(() => {
    if (!scrollContainerRef.current || !stepItemRefs.current) return;

    // find the index of the ongoing step
    let targetIndex = steps.findIndex(s => s.status === 'ongoing');
    if (targetIndex < 0) {
      // if no ongoing, find the last done
      targetIndex = steps.reduce((acc, s, idx) => {
        if (s.status === 'done') return idx;
        return acc;
      }, -1);
    }
    if (targetIndex < 0) return; // none found

    // get the step item
    const itemEl = stepItemRefs.current[targetIndex]?.current;
    if (!itemEl) return;

    // center that item horizontally in the scroll container
    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();

    // Calculate the offset so that item is centered
    const containerCenter = containerRect.width / 2;
    const itemCenter = itemRect.width / 2;
    const itemOffset = (itemRect.x - containerRect.x) + itemCenter;
    const scrollLeft = itemOffset - containerCenter;

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }, [steps]);

  return (
    <div
      className="d-flex align-items-center bottombar"
    >
      {/* Left button (50x50) with dropup */}
      <div className="dropup" style={{ width: '575x', height: '75px' }}>
        <button
          className="btn p-0"
          style={{ width: '75px', height: '75px', borderRadius: 0 }}
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title="Menu"
          aria-label="Menu"
        >
          <i className="bi bi-list" style={{ fontSize: '1.4rem' }}></i>
        </button>
        <ul className="dropdown-menu">
          <li>
            <Link className="dropdown-item d-flex align-items-center" to="/">
              <i className="bi bi-house me-2"></i>
              Home
            </Link>
          </li>
          <li>
            <Link
            onClick={handleProfileClick}
            className="dropdown-item d-flex align-items-center"
            style={{ marginRight: '1rem'}}
          >
            <i className="bi bi-person me-2"></i>
            {displayName}
          </Link>
        </li>
        <li>
          <Link onClick={handleLogout} className="dropdown-item d-flex align-items-center">
            <i className="bi bi-box-arrow-right me-2"></i>
            Log Out
          </Link>
        </li>

        </ul>
      </div>

      {/* Asset nav or workflow steps */}
      <div
        className="flex-grow-1 overflow-auto d-flex align-items-center"
        ref={scrollContainerRef}
        style={{ height: steps.length > 0 ? '50px' : '75px' }}
      >
        {steps.length === 0 ? (
          <AssetBottomNav />
        ) : (
        <div id="stepsRow" className="d-flex align-items-center" style={{ height: '50px' }}>
          {steps.map((step, idx) => {
            // color for the step number
            let stepNumClass = 'text-secondary'; // upcoming
            if (step.status === 'done') stepNumClass = 'text-success';
            if (step.status === 'ongoing') stepNumClass = 'text-dark';

            // only done steps have a valid link
            const clickable = (step.status === 'done');
            // store a ref for each item
            stepItemRefs.current[idx] = stepItemRefs.current[idx] || React.createRef();

            return (
              <React.Fragment key={idx}>
                <div
                  ref={stepItemRefs.current[idx]}
                  className="px-2 d-flex align-items-center"
                  style={{ minWidth: '120px', height: '50px' }}
                >
                  <span className={`${stepNumClass} me-1`}>
                    {String(step.stepNumber).padStart(2, '0')}
                  </span>
                  {clickable ? (
                    <Link
                      to={step.link || '#'}
                      className="small text-decoration-none text-body"
                    >
                      {step.label}
                    </Link>
                  ) : (
                    <span className="small text-muted">{step.label}</span>
                  )}
                </div>
                {/* vertical divider, half height */}
                {idx < steps.length - 1 && (
                  <div
                    style={{
                      width: '1px',
                      height: '50%',
                      backgroundColor: '#ccc'
                    }}
                  ></div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
